import type {
  ServerFrame, Message, ManifestAction, MessageContent,
  ConversationId, MessageId, UserId, Subject,
} from './protocol/index.js'

export type SendStatus = 'pending' | 'sent' | 'delivered' | 'read'

export interface RenderMessage extends Message {
  clientMsgId?: string
  status?:      SendStatus
}

/** Pure, DOM-free conversation state. Feed it ServerFrames (and local optimistic
 *  sends); read an ordered, de-duplicated view out. Ordering is by `seq`; the
 *  same message arriving twice (live + sync on reconnect) is collapsed by id —
 *  the structural fix for the old duplicate-bubble bug. */
export class ChatStore {
  conversationId?: ConversationId
  state = ''
  version = 0
  hasMoreHistory = false
  lastReadByOthers = 0
  assignedAgentId: UserId | undefined
  accent: string | undefined
  subject: Subject | undefined
  name: string | undefined
  e2e = false
  offline = false
  offlineMessage = ''
  launcherMessage: { title: string; subtitle?: string } | null = null
  /** Server-authored prompts shown above the composer until the guest's first
   *  message (manifest `suggestedQuestions`). Host `quickReplies` win. */
  suggestedQuestions: string[] = []
  preChat: import('./protocol/frames.js').PreChatConfig | null = null
  whiteLabel = false
  readonly typing = new Set<string>()
  readonly online = new Set<string>()
  /** Live sentiment of the guest's latest message (agent-side only). */
  sentiment: 'positive' | 'neutral' | 'frustrated' | undefined
  sentimentScore: number | undefined

  private actions: ManifestAction[] = []
  private readonly byId = new Map<string, RenderMessage>()
  private readonly keyByClient = new Map<string, string>()
  private _maxSeq = 0
  private _sorted: RenderMessage[] | null = null

  constructor(readonly me: UserId) {}

  messages(): RenderMessage[] {
    if (!this._sorted) {
      this._sorted = [...this.byId.values()].sort((a, b) => {
        const ap = a.status === 'pending', bp = b.status === 'pending'
        if (ap !== bp) return ap ? 1 : -1
        if (ap && bp) return a.ts - b.ts
        return a.seq - b.seq
      })
    }
    return this._sorted
  }

  visibleActions(): ManifestAction[] {
    return this.actions.filter(a => !a.availableInStates || a.availableInStates.includes(this.state))
  }

  /** Any manifest action by id, regardless of state/surface — used to open the
   *  form a `form`-kind message points at (search pre-fill). */
  actionById(id: string): ManifestAction | undefined {
    return this.actions.find(a => a.id === id)
  }

  highestSeq(): number { return this._maxSeq }

  addOptimistic(clientMsgId: string, content: MessageContent): RenderMessage {
    const msg: RenderMessage = {
      id: clientMsgId as MessageId, conversationId: this.conversationId as ConversationId,
      seq: 0, senderId: this.me, senderRole: 'guest', content, ts: Date.now(),
      clientMsgId, status: 'pending',
    }
    this.byId.set(clientMsgId, msg)
    this.keyByClient.set(clientMsgId, clientMsgId)
    this._sorted = null
    return msg
  }

  apply(frame: ServerFrame): void {
    switch (frame.type) {
      case 'opened':
        this.conversationId = frame.conversation.id
        this.state = frame.conversation.state
        if (frame.subject) this.subject = frame.subject
        return
      case 'manifest':
        this.actions = frame.actions
        this.version = frame.version
        if (frame.name) this.name = frame.name
        if (frame.theme?.accent) this.accent = frame.theme.accent
        if (frame.e2e) this.e2e = true
        // Track the manifest EXACTLY: a sticky `offline` (only ever set, never
        // cleared) kept the widget in away-mode for the whole session once a
        // single manifest said so — which used to hide the composer entirely.
        this.offline = frame.offline === true
        this.offlineMessage = frame.offlineMessage ?? ''
        if (frame.launcherMessage) this.launcherMessage = frame.launcherMessage
        if (frame.suggestedQuestions) this.suggestedQuestions = frame.suggestedQuestions
        if (frame.whiteLabel) this.whiteLabel = true
        if (frame.preChat) this.preChat = frame.preChat
        return
      case 'message':
        this.upsert({ ...frame.message })
        return
      case 'ack': {
        const key = this.keyByClient.get(frame.clientMsgId)
        const msg = key ? this.byId.get(key) : undefined
        if (msg && key) {
          this.byId.delete(key)
          const confirmed: RenderMessage = { ...msg, id: frame.messageId, seq: frame.seq, ts: frame.ts, status: 'sent' }
          this.byId.set(frame.messageId, confirmed)
          this.keyByClient.set(frame.clientMsgId, frame.messageId)
          if (frame.seq > this._maxSeq) this._maxSeq = frame.seq
        }
        this._sorted = null
        return
      }
      case 'delivered':
        this.markOwnStatus(frame.seq, 'delivered')
        return
      case 'read':
        if (frame.by !== this.me) {
          this.lastReadByOthers = Math.max(this.lastReadByOthers, frame.seq)
          this.markOwnStatus(frame.seq, 'read')
        }
        return
      case 'sync':
        for (const m of frame.messages) this.upsert({ ...m })
        return
      case 'history':
        for (const m of frame.messages) this.upsert({ ...m })
        this.hasMoreHistory = frame.hasMore
        return
      case 'typing':
        if (frame.userId !== this.me) {
          if (frame.isTyping) this.typing.add(frame.userId)
          else this.typing.delete(frame.userId)
        }
        return
      case 'reaction': {
        const m = this.byId.get(frame.messageId)
        if (!m) return
        const reactions: Record<string, UserId[]> = { ...(m.reactions ?? {}) }
        const users = (reactions[frame.emoji] ?? []).filter(u => u !== frame.by)
        if (!frame.removed) users.push(frame.by)
        if (users.length) reactions[frame.emoji] = users; else delete reactions[frame.emoji]
        this.byId.set(frame.messageId, { ...m, reactions })
        this._sorted = null
        return
      }
      case 'edited': {
        const m = this.byId.get(frame.messageId)
        if (m) { this.byId.set(frame.messageId, { ...m, content: frame.content, editedAt: frame.editedAt }); this._sorted = null }
        return
      }
      case 'deleted': {
        const m = this.byId.get(frame.messageId)
        if (m) { this.byId.set(frame.messageId, { ...m, deletedAt: frame.ts }); this._sorted = null }
        return
      }
      case 'state':
        this.state = frame.state
        return
      case 'assigned':
        this.assignedAgentId = frame.agentId ?? undefined
        return
      case 'presence':
        if (frame.status === 'online') this.online.add(frame.userId)
        else this.online.delete(frame.userId)
        return
      case 'subjectState':
      case 'invoked':
      case 'authed':
      case 'error':
      case 'pong':
        return
      case 'sentiment':
        this.sentiment = frame.label
        this.sentimentScore = frame.score
        return
      default:
        return
    }
  }

  private upsert(msg: RenderMessage): void {
    const existing = this.byId.get(msg.id)
    this.byId.set(msg.id, existing ? { ...existing, ...msg } : msg)
    if (msg.seq > this._maxSeq) this._maxSeq = msg.seq
    this._sorted = null
  }

  private markOwnStatus(uptoSeq: number, status: SendStatus): void {
    const targetRank = rank(status)
    let changed = false
    for (const [k, m] of this.byId) {
      if (m.senderId !== this.me || m.seq <= 0 || m.seq > uptoSeq) continue
      if (rank(m.status) >= targetRank) continue  // already at or above target — skip
      this.byId.set(k, { ...m, status })
      changed = true
    }
    if (changed) this._sorted = null
  }
}
function rank(s: SendStatus | undefined): number {
  switch (s) { case 'read': return 3; case 'delivered': return 2; case 'sent': return 1; default: return 0 }
}
